import React, { useState, useEffect, useRef, Suspense } from "react";
import { useDispatch } from "react-redux";
import {
  useCreateRefundAllMutation,
  useCreateRefundMutation,
} from "../../../Services/RefundService";
import {
  OrderItem,
  useLazyGetCustomerOrderPinQuery,
  useLazyGetOrderDetailsQuery,
  useLazyDownloadCreditMemoQuery,
  useGetOrderCreditMemosQuery,
  useLazyDownloadRefundInvoiceQuery,
  useGetOrderRefundInvoicesQuery,
} from "../../../Services/OrderService";
import { useParams } from "react-router-dom";
import Breadcrumbs from "../../../Components/BreadCrumb";
import { FaEye, FaPhoneAlt, FaUsers, FaFileAlt } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import DealsSection from "../../../Components/DealsSection/DealsSection";
import Modal from "../../../Components/Modal";
import ProductReview from "../ProductReview";
import Utils from "../../../Utils/CommonProgram";
import { BsBusFrontFill } from "react-icons/bs";
import { MdContactPage, MdEmail, MdCancel, MdKeyboardArrowDown, MdEventSeat } from "react-icons/md";
import JourneyBreakdown, { buildTrips } from "./JourneyBreakdown";
import { IoIosInformationCircleOutline } from "react-icons/io";
import { IoTicketSharp } from "react-icons/io5";
import { useToast } from "../../../Helpers/Hooks/useToast";
import { useCancelOrderMutation } from "../../../Services/BusBookingService";
import { useGetBankDetailsQuery } from "../../../Services/SettingsService";
import { FaRegCopy } from "react-icons/fa6";
import dayjs from "dayjs";
import ViewPinsModal from "../ViewPinsModal";
import { useLazyGetProductDetailsQuery } from "../../../Services/ProductService";
import Config from "../../../Config";
import { wasPaid } from "../../../Helpers/orderPaymentState";
import {
  deliveryFailureMessage,
  refundReasonForFailure,
} from "../../../Utils/deliveryFailureMessage";

const formatOrderStatus = (status: any) => {
  return status
    .split("_") // Split by underscore
    .map((word: any) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" "); // Join the words with space
};
/**
 * Has this journey already left?
 *
 * Cancel Booking was offered on any delivered travel item that had a QR code,
 * with nothing looking at the departure date -- so ORDE-136 (departed 8 Sep
 * 21:48) and ORDE-143 (departed 9 Sep 20:30) still offered it days later. The
 * customer travelled; there is nothing to cancel.
 *
 * Two sources for the time. FlixBus's own `trips[].departure.timestamp` is an
 * epoch and exact. The fallback, `flixbusData.departure`, is a bare
 * "2026-09-08 21:48" carrying no timezone -- it is the station's local time,
 * and read in a browser set to IST it lands 3.5 hours out. So the fallback only
 * counts as departed once it is more than twelve hours past, which no timezone
 * offset can explain away. Anything closer than that stays cancellable here and
 * is settled by the server, and then by FlixBus, which owns the real
 * cancellation window. Hiding the button from someone entitled to a refund is
 * the worse mistake.
 */
const IMPRECISE_GRACE_MS = 12 * 60 * 60 * 1000;

const hasJourneyDeparted = (orderItem: any): boolean => {
  const stamp =
    orderItem?.flixbusData?.orderInfo?.order?.trips?.[0]?.departure?.timestamp;
  if (typeof stamp === "number" && Number.isFinite(stamp)) {
    return Date.now() > stamp * 1000;
  }
  const raw = orderItem?.flixbusData?.departure;
  if (typeof raw === "string" && raw.trim()) {
    const ms = new Date(raw.trim().replace(" ", "T")).getTime();
    if (Number.isFinite(ms)) return Date.now() > ms + IMPRECISE_GRACE_MS;
  }
  return false; // unknown is not departed
};

/**
 * The seats FlixBus actually gave this booking.
 *
 * Seat selection is optional, so a customer can book without choosing one and
 * FlixBus allocates a seat itself. It tells us which, in
 * `trips[].seats_per_relation[].seats[].label` -- and nothing read it. Across
 * 102 bookings, 89 carry a label, and 68 of those belong to customers who
 * picked no seat at all. They were shown "Unassigned" on the ticket while the
 * order record held "7D".
 *
 * `is_auto_assigned` is not usable to tell chosen from allocated -- it is false
 * on all 89 -- so whether the customer chose comes from our own
 * seatAndTicketInfo instead.
 */
/*
 * The full journey -- legs, changes, wait times and the seat on each ride.
 * Falls back to the flat seat list below for a booking it cannot read.
 */
const getAssignedSeats = (
  orderItem: any,
): { labels: string[]; chosen: boolean } => {
  const labels: string[] = [];
  const trips = orderItem?.flixbusData?.orderInfo?.order?.trips;
  if (Array.isArray(trips)) {
    trips.forEach((t: any) =>
      (t?.seats_per_relation || []).forEach((r: any) =>
        (r?.seats || []).forEach((seat: any) => {
          if (seat?.label) labels.push(String(seat.label));
        }),
      ),
    );
  }
  const picked = (orderItem?.flixbusData?.seatAndTicketInfo || []).reduce(
    (n: number, s: any) => n + ((s?.seats || []).length),
    0,
  );
  return { labels: Array.from(new Set(labels)), chosen: picked > 0 };
};

/**
 * Adult or child, as the ticket was sold.
 *
 * The passenger rows showed the name and the gender but never the fare type,
 * and a child ticket is priced differently and carries its own rules -- FlixBus
 * caps a child fare at 14. Somebody checking their booking could not tell which
 * of their passengers had been booked as which.
 *
 * `type` is what we sent and what FlixBus confirmed back, so it is the ticket
 * that was actually bought rather than a guess from a date of birth.
 */
const fareType = (p: any): string => {
  const t = String(p?.type || p?.product_type || "").toLowerCase();
  if (t.includes("child")) return "Child";
  if (t.includes("adult")) return "Adult";
  return "";
};

/**
 * Which seat is on which part of the journey.
 *
 * A booking with changes has a seat per ride, and they were printed as one
 * list -- "2C, 1D, 3D, 20D, 14B, 17C" on a round trip with two changes each
 * way. Six seat numbers and nothing saying which coach, which bus, or even
 * which direction. A passenger cannot use that.
 *
 * FlixBus already says it: `seats_per_relation` gives the seat against the
 * pair of stations it covers, and the station names are on the trip itself
 * (`departure_station`, `arrival_station`) and on each `transfers[].station`.
 * Joining those gives the leg-by-leg answer.
 *
 * Returns one entry per trip -- outbound, then return -- each with its legs in
 * travel order. Anything missing is left out rather than guessed, so a booking
 * FlixBus described differently degrades to the plain list above.
 */
const getSeatsByLeg = (
  orderItem: any,
): { title: string; legs: { from: string; to: string; vehicle: string; labels: string[] }[] }[] => {
  const trips = orderItem?.flixbusData?.orderInfo?.order?.trips;
  if (!Array.isArray(trips) || !trips.length) return [];

  return trips
    .map((t: any, index: number) => {
      // Station id -> name, from the two endpoints plus every transfer.
      const names = new Map<string, string>();
      const add = (st: any) => {
        if (st?.id != null && st?.name) names.set(String(st.id), String(st.name));
      };
      add(t?.departure_station);
      add(t?.arrival_station);
      (t?.transfers || []).forEach((tr: any) => add(tr?.station));

      const legs = (t?.seats_per_relation || [])
        .map((r: any) => {
          const labels = (r?.seats || [])
            .map((seat: any) => seat?.label)
            .filter(Boolean)
            .map(String);
          if (!labels.length) return null;
          return {
            from: names.get(String(r?.from)) || "",
            to: names.get(String(r?.to)) || "",
            // "train" or "bus" -- the same distinction the search results make.
            vehicle: String((r?.seats || [])[0]?.vehicle || ""),
            labels,
          };
        })
        .filter(Boolean) as { from: string; to: string; vehicle: string; labels: string[] }[];

      if (!legs.length) return null;

      // Two trips on a round trip: the first is the way out.
      const title =
        trips.length > 1 ? (index === 0 ? "Outbound" : "Return") : "";
      return { title, legs };
    })
    .filter(Boolean) as {
    title: string;
    legs: { from: string; to: string; vehicle: string; labels: string[] }[];
  }[];
};

const MyOrderDetails = () => {
  const { orderNumber } = useParams();
  /*
   * May this order still be queried?
   *
   * Support answers questions about recent purchases; the server holds the
   * rule in helpOrders.ts as HELP_ORDER_WINDOW_DAYS = 7, and refuses anything
   * older on submit. This mirrors it so the button is not offered for an
   * order the form would then reject -- a dead end is worse than no button.
   *
   * Kept as a plain constant rather than imported: the portal and the API do
   * not share code, and a seven that drifts is a smaller problem than a build
   * that cannot resolve the import. If the window changes, both move.
   */
  const HELP_WINDOW_DAYS = 7;
  const dispatch = useDispatch();
  const toast = useToast();
  const navigate = useNavigate();
  const [orderItem, setOrderItem] = useState<any>();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCancelOption, setIsCancelOption] = useState<boolean>(false);
  const [flixOrderDetails, setFlixOrderDetails] = useState<any>({});
  const [seatBookingDetails, setSeatBookingDetails] = useState<any>([]);
  const [selectedBatch, setSelectedBatch] = useState<any>(null);
  const [isConfirming, setIsConfirming] = useState<boolean>(false);
  const [ticketDownloading, setTicketDownloading] = useState<boolean>(false);

  const [orderNumberForRefundAll, setOrderNumberForRefundAll] =
    useState<any>(null);

  const [orderItemNumberForRefundAll, setOrderItemNumberForRefundAll] =
    useState<any>(null);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isOpenForReview, setIsOpenForReview] = useState<boolean>(false);
  const [orderDetails, setOrderDetails] = useState<any>({});
  const [pinData, setPinData] = useState<any>(null);
  const [pinLoading, setPinLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [cancelOrder] = useCancelOrderMutation();
  const [getProductDetailsTrigger] = useLazyGetProductDetailsQuery();
  const [itemDetailsLoading, setItemDetailsLoading] = useState(false);
  const [busOrderInfo, setBusOrderInfo] = useState<any>(null);
  const [busOrderInfoLoading, setBusOrderInfoLoading] = useState(false);
  const [showBankDetailsPopup, setShowBankDetailsPopup] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [activeDocTab, setActiveDocTab] = useState(0);
  const [docLoading, setDocLoading] = useState<string | null>(null);

  // const [getMyAllOrdersTrigger, { data, reset }] = useLazyGetMyAllOrdersQuery();

  const [
    getCustomerOrderPin,
    // { data: pinData, isLoading: pinLoading, reset: resetOldPin, isFetching },
  ] = useLazyGetCustomerOrderPinQuery();

  const [getOrderDetailsTrigger, { data: orderData, isLoading }] =
    useLazyGetOrderDetailsQuery();

  const { data: bankDetails } = useGetBankDetailsQuery();

  const [downloadCreditMemoTrigger] = useLazyDownloadCreditMemoQuery();
  const [downloadRefundInvoiceTrigger] = useLazyDownloadRefundInvoiceQuery();
  const { data: creditMemos } = useGetOrderCreditMemosQuery(orderNumber || "", { skip: !orderNumber });
  const { data: refundInvoices } = useGetOrderRefundInvoicesQuery(orderNumber || "", { skip: !orderNumber });

  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [refundReason, setRefundReason] = useState("");

  const [createRefund, { isLoading: isRefundLoading }] =
    useCreateRefundMutation();

  const [createRefundForAll, { isLoading: isRefundAllLoading }] =
    useCreateRefundAllMutation();

  const Star = ({ color }: { color?: string }) => {
    return (
      <svg
        className={`w-3 h-3`}
        fill={color || "#facc15"}
        viewBox="0 0 14 13"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M7 0L9.4687 3.60213L13.6574 4.83688L10.9944 8.29787L11.1145 12.6631L7 11.2L2.8855 12.6631L3.00556 8.29787L0.342604 4.83688L4.5313 3.60213L7 0Z" />
      </svg>
    );
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "paid":
      case "delivered":
        return "bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300";
      case "refunded":
      case "refund_completed":
        return "bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300";
      case "refund_requested":
      case "partial_refund_requested":
      case "pending":
      case "in_progress":
      case "unpaid":
        return "bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300";
      case "pending_retry":
        return "bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300";
      case "partially_delivered":
        return "bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300";
      case "refund_processing":
      case "partial_refund_processing":
        return "bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300";
      case "delivery_failed":
        return "bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300";
      case "cancelled":
      case "rejected":
      case "failed":
      case "refund_declined":
        return "bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300";
      default:
        return "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300";
    }
  };

  const handleCancelBooking = async () => {
    setIsConfirming(true);
    const { id, token } = flixOrderDetails;
    const tickets = seatBookingDetails
      .filter((item: any) => item.ticket)
      .map((item: any) => item.ticket);
    try {
      const response = await cancelOrder({
        orderId: id,
        token,
        orderNumber: orderNumber as string,
        body: {
          items: tickets,
          refundType: "cash",
          context:
            "Following tickets are being canceled due to lack of capacity",
        },
      }).unwrap();
      // Success toast
      setIsCancelOption(false);
      // Not awaited, so the enclosing try/catch could never catch this -- an
      // error result became an unhandled rejection. Result is unused; drop unwrap.
      getOrderDetailsTrigger(orderNumber);
      toast.success(
        response?.message ||
          "Booking cancelled. Any refund will be reviewed and returned to your original payment method.",
      );
      setIsConfirming(false);
    } catch (error: any) {
      // If API returns error message
      const errorMsg =
        error?.data?.message ||
        error?.error ||
        "Failed to cancel booking. Please try again.";

      toast.error(errorMsg);
      setIsCancelOption(false);
      setIsConfirming(false);
    }
  };

  const downloadTicket = async (orderItem: any) => {
    const id = orderItem?.flixbusData?.flixOrderDetails?.id;
    const token = orderItem?.flixbusData?.flixOrderDetails?.token;
    if (!id || !token) return;

    setTicketDownloading(true);
    try {
      const res = await fetch(
        `${Config.BASE_URL}/bus-ticket-download?orderId=${id}&downloadHash=${token}`
      );
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `FlixBus-Ticket-${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error("Failed to download ticket. Please try again.");
    } finally {
      setTicketDownloading(false);
    }
  };
  const handleViewDocument = async (type: "creditMemo" | "refundInvoice", number: string, lang: "en" | "de") => {
    const loadingKey = `${type}-${number}-${lang}`;
    setDocLoading(loadingKey);
    try {
      let result: any;
      if (type === "creditMemo") {
        result = await downloadCreditMemoTrigger({ creditMemoNumber: number, lang }).unwrap();
      } else {
        result = await downloadRefundInvoiceTrigger({ refundInvoiceNumber: number, lang }).unwrap();
      }
      if (result) {
        const blob = result instanceof Blob ? result : new Blob([result], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      }
    } catch (error) {
      console.error(`Error downloading ${type}:`, error);
    } finally {
      setDocLoading(null);
    }
  };

  const emptyStars = (rating: number) =>
    5 - Math.floor(rating) - (rating % 1 !== 0 ? 1 : 0);

  // const copyClipBoard = (data: any) => {
  //   navigator.clipboard.writeText(data);
  //   dispatch(
  //     showSnackbar({
  //       message: "Pin copied to clipboard",
  //       severity: "success",
  //     })
  //   );
  // };

  const showPinDetailsModal = async (orderItem: OrderItem) => {
    // e.stopPropagation();
    setPinData(null);
    setPinLoading(true);
    setOrderDetails(orderItem);
    setIsOpen(true);

    try {
      const result = await getCustomerOrderPin(orderItem._id);
      if ("data" in result && result.data.status === "success") {
        setPinData(result?.data.data);
      } else if ("error" in result) {
        console.error("Error fetching pin:", result.error);
      }
    } catch (err) {
      console.error("Unexpected error:", err);
    } finally {
      setPinLoading(false);
    }
  };

  const handleRefundForAll = async (
    order: any,
    orderNumber: string,
    itemNumber: string,
    reason: string
  ) => {
    try {
      const response = await createRefundForAll({
        orderNumber: orderNumber,
        itemNumber: itemNumber,
        customerReason: reason || "customer_cancelled_order",
        paymentReference: order.paymentId,
      }).unwrap();

      setIsRefundModalOpen(false);
      setIsOpen(false);
      getOrderDetailsTrigger(orderNumber);
      toast.success(response?.message || "Refund Requested Successfully.");

      setRefundReason("");
      // Clear refund-all routing so a later single-pin refund isn't misrouted.
      setOrderNumberForRefundAll(null);
      setOrderItemNumberForRefundAll(null);
    } catch (error: any) {
      /*
       * Say something. This used to console.error and stop, so a failed request
       * left the dialog sitting there with the Submit button doing nothing --
       * which is exactly how it was reported on ORDE-221 ("nothing is
       * happening when I click submit"), while the server was answering 500.
       *
       * The dialog stays open on purpose: the typed reason is still in it, and
       * closing it would throw that away along with the explanation.
       */
      console.error("Error creating refund:", error);
      toast.error(
        error?.data?.message ||
          "We could not submit your refund request. Please try again, or contact support if it keeps failing.",
        5000,
      );
    }
  };

  const handleRefund = async (
    order: any,
    // item: any,
    customerReason: string,
    batch: string
  ) => {
    try {
      const response = await createRefund({
        orderItemId: orderDetails._id,
        orderNumber: orderDetails.orderNumber,
        itemNumber: orderDetails.itemNumber,
        paymentReference: order.paymentId,
        amount: orderDetails.price,
        currency: "EUR",
        customerReason: customerReason || "customer_cancelled_order",
        batch,
        productId: orderDetails.productId,
        productDenominationsku: orderDetails.productDenominationsku,
      }).unwrap();

      setIsRefundModalOpen(false);
      setIsOpen(false);
      getOrderDetailsTrigger(orderNumber);
      toast.success(response?.message || "Refund Requested Successfully.");

      setRefundReason("");
      setSelectedBatch(null);
    } catch (error: any) {
      // Same as handleRefundForAll above: never fail silently.
      console.error("Error creating refund:", error);
      toast.error(
        error?.data?.message ||
          "We could not submit your refund request. Please try again, or contact support if it keeps failing.",
        5000,
      );
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [dispatch]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handlePrintInvoice = async (order: any) => {
    const orderNum = order.orderNumber;
    if (!orderNum) {
      console.error("No order number found");
      return;
    }

    try {
      const raw = localStorage.getItem(
        `${process.env.REACT_APP_STORAGE_KEY}/accessToken`
      );
      const token = raw ? JSON.parse(raw) : null;

      const response = await fetch(
        `${Config.BASE_URL}/invoice-download/${orderNum}/en`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch invoice");

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const win = window.open(blobUrl);
      if (win) {
        win.onload = () => {
          win.focus();
          win.print();
        };
      }
    } catch (err) {
      console.error("Error loading PDF:", err);
    }
  };

  useEffect(() => {
    if (orderNumber) {
      // See above: unwrap here turned a 403 (banned account) into an unhandled
      // rejection and the dev overlay covered the page.
      getOrderDetailsTrigger(orderNumber);
    }
  }, [orderNumber]);

  // Fetch bus order info (T&C / cancellation policy) on demand
  useEffect(() => {
    if (orderData?.orderItems) {
      const travelItem = orderData.orderItems.find(
        (item: any) => item.sourceType === "travel" && item.flixbusData?.flixOrderDetails
      );
      if (travelItem && !travelItem.flixbusData?.orderInfo && !busOrderInfo) {
        const { id, token } = travelItem.flixbusData.flixOrderDetails;
        if (id && token) {
          setBusOrderInfoLoading(true);
          fetch(`${Config.BASE_URL}/bus-order-info?orderId=${id}&downloadHash=${token}`)
            .then((res) => res.json())
            .then((result) => {
              setBusOrderInfo(result?.data || result);
            })
            .catch(() => {})
            .finally(() => setBusOrderInfoLoading(false));
        }
      }
    }
  }, [orderData]);

  /*
   * Within the window, and only once we actually have the order. dayjs diff
   * rather than a raw subtraction so a missing or unparseable createdAt does
   * not quietly become "0 days old" and show the button on everything.
   */
  const canRaiseQuery = Boolean(
    orderData?.orderNumber &&
      orderData?.createdAt &&
      dayjs().diff(dayjs(orderData.createdAt), "day") < HELP_WINDOW_DAYS,
  );

  const handleNavigateToProductDetails = async (orderItem: any) => {
    try {
      setItemDetailsLoading(true);
      const response = await getProductDetailsTrigger(
        orderItem.productId
      ).unwrap();

      if (
        response.productDenominations &&
        response.productDenominations.length > 0
      ) {
        if (response.productType === "cards") {
          navigate(
            `/gift-cards/product-details/${orderItem.productId}/${response.productName}`
          );
        } else if (response.productType === "phone-refill") {
          navigate(
            `/refill-products-details/${orderItem.productId}/${response.productName}`
          );
        }
      } else {
        toast.error("Product is out of stock");
      }
    } catch (error) {
      toast.error("Product is currently unavailable");
    } finally {
      setItemDetailsLoading(false);
    }
  };

  // show the one loader with somew blur effect throuht the screen while fetching the product data

  return (
    <>
      {itemDetailsLoading ? (
        <div className="fixed inset-0 flex items-center justify-center bg-white dark:bg-dark-background bg-opacity-80 dark:bg-opacity-80 z-50 flex-col">
          <div className="w-16 h-16 border-4 border-blue-500 border-solid border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="space-y-4 w-full p-5 pl-10 pr-10">
          <div className="flex justify-between mb-[-10px]">
            <Breadcrumbs
              crumbs={[
                { label: "Home", path: "/" },
                { label: "My Orders", path: "/my-orders" },
                { label: "Order Details" }, // current page, no path
              ]}
            />
            {(wasPaid(orderData?.paymentStatus) && orderData?.invoiceUrlEn) ||
            orderData?.invoiceUrlDe ? (
              <div
                ref={dropdownRef}
                className="relative inline-block text-left"
              >
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="inline-flex justify-center w-full rounded-md border border-gray-300 dark:border-dark-foreground shadow-sm px-3 py-1 bg-white dark:bg-dark-foreground text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <span>Invoice</span>
                  <MdKeyboardArrowDown size={21} />
                </button>

                {isDropdownOpen && (
                  <div className="origin-top-right absolute right-0 mt-2 w-40 rounded-md shadow-lg bg-white dark:bg-dark-foreground ring-1 ring-black ring-opacity-5 z-10">
                    <div className="py-1">
                      <button
                        onClick={() => {
                          const url =
                            window.location.pathname +
                            `#${`/order-invoice/${orderData?.orderNumber}`}`;
                          window.open(url, "_blank");
                          setIsDropdownOpen(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        View
                      </button>
                      <button
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => handlePrintInvoice(orderData)}
                      >
                        Print
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
          {isLoading ? (
            <div className="flex items-center justify-center min-h-[300px]">
              <div className="w-16 h-16 border-4 border-blue-500 border-solid border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div>
              <div className="rounded-lg border dark:border-dark-foreground p-6 bg-white dark:bg-dark-foreground space-y-4 shadow-sm">
                {orderData && Array.isArray(orderData?.orderItems) && (
                  <div className="flex flex-wrap justify-between gap-4">
                    {/* Order Info */}
                    <div className="min-w-[220px]">
                      <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-1">
                        Order #{orderData.orderNumber}
                      </h2>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Placed on:{" "}
                        <span className="text-gray-800 dark:text-gray-300 font-medium">
                          {dayjs(
                            orderData?.createdAt,
                            "YYYY-MM-DDTHH:mm:ss.SSSZ"
                          ).format("DD.MM.YYYY, HH.mm.ss")}
                        </span>
                      </p>
                      {/*
                        Raising a query about this order, from the order.
                        
                        Help already existed at /helps and was linked from the
                        header, Buy Now and Checkout -- but not from the one
                        screen where somebody is looking at the order they have
                        a problem with. They had to go to Help and find it
                        again in a dropdown. Reported from the demo call.
                        
                        Only inside the seven-day window the server enforces.
                        Offering it on an older order would send the customer
                        to a form that refuses the order they arrived with.
                      */}
                      {canRaiseQuery ? (
                        <Link
                          to={`/helps?order=${encodeURIComponent(
                            orderData.orderNumber,
                          )}`}
                          className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-light-primary-500 hover:text-light-primary-600 dark:text-light-primary-400"
                        >
                          <IoTicketSharp size={16} />
                          Need help with this order?
                        </Link>
                      ) : null}

                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                        Payment Status:{" "}
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-semibold ${
                            orderData.paymentStatus === "paid"
                              ? "bg-green-100 text-green-700"
                              : orderData.paymentStatus === "refunded" ||
                                orderData.paymentStatus === "partial_refund"
                              ? "bg-blue-100 text-blue-700"
                              : orderData.paymentStatus === "rejected"
                              ? "bg-red-100 text-red-700"
                              : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {formatOrderStatus(orderData.paymentStatus)}
                        </span>
                      </p>
                      {orderData?.paymentMethod === "BankTransfer" && orderData?.referenceNumber && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                          Reference Number:{" "}
                          <span className="text-gray-800 dark:text-gray-300 font-semibold">
                            {orderData.referenceNumber}
                          </span>
                        </p>
                      )}
                      {orderData?.paymentMethod === "BankTransfer" && bankDetails && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 flex items-center gap-2">
                          Bank Details:{" "}
                          <button
                            onClick={() => setShowBankDetailsPopup(true)}
                            className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-semibold transition"
                            title="View Bank Details"
                          >
                            <FaEye className="w-4 h-4" />
                            <span className="text-sm">View</span>
                          </button>
                        </p>
                      )}
                    </div>
                    {/* Shipping Address */}
                    <div className="min-w-[220px]">
                      <h3 className="text-md font-semibold text-gray-800 dark:text-white mb-2">
                        Shipping Address
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-snug">
                        {orderData.shippingAddress?.street},<br />
                        {orderData.shippingAddress?.city} -{" "}
                        {orderData.shippingAddress?.postalCode},<br />
                        {orderData.shippingAddress?.state},{" "}
                        {orderData.shippingAddress?.country}
                      </p>
                    </div>
                    {/* Billing Address */}
                    <div className="min-w-[220px]">
                      <h3 className="text-md font-semibold text-gray-800 dark:text-white mb-2">
                        Billing Address
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-snug">
                        {orderData.billingAddress?.street},<br />
                        {orderData.billingAddress?.city} -{" "}
                        {orderData.billingAddress?.postalCode},<br />
                        {orderData.billingAddress?.state},{" "}
                        {orderData.billingAddress?.country}
                      </p>
                    </div>
                    {/* Order Summary */}
                    <div className="min-w-[220px]">
                      <h3 className="text-md font-semibold text-gray-800 dark:text-white mb-2">
                        Order Summary
                      </h3>
                      <div className="text-sm text-gray-600 dark:text-gray-400 flex justify-between">
                        <span>Total Items:</span>
                        <span className="font-medium text-black dark:text-white">
                          {orderData.totalQuantity}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 flex justify-between">
                        <span>Subtotal:</span>
                        <span className="font-medium text-black dark:text-white">
                          {Utils.formatEuro(orderData.subTotalAmount)}
                        </span>
                      </div>
                      {orderData.totalDiscount && Number(orderData.totalDiscount) > 0 ? (
                        <div className="text-sm text-gray-600 dark:text-gray-400 flex justify-between">
                          <span className="text-green-600">Discount:</span>
                          <span className="font-medium text-green-600">
                            (-) {Utils.formatEuro(orderData.totalDiscount)}
                          </span>
                        </div>
                      ) : null}
                      <div className="text-sm text-gray-600 dark:text-gray-400 flex justify-between">
                        <span>Total VAT:</span>
                        <span className="font-medium text-black dark:text-white">
                          {Utils.formatEuro(orderData.totalTaxAmount)}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 flex justify-between">
                        <span>Service Fee:</span>
                        <span className="font-medium text-black dark:text-white">
                          {Utils.formatEuro(
                            Number(
                              orderData.serviceFee + orderData.taxOnServiceFee
                            )
                          )}
                        </span>
                      </div>
                      <div className="text-sm text-gray-800 dark:text-white font-bold flex justify-between mt-1">
                        <span>Total:</span>
                        <span>{Utils.formatEuro(orderData.totalAmount)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {orderData &&
                Array.isArray(orderData?.orderItems) &&
                orderData?.orderItems.map((orderItem: any, index: number) => {
                  return (
                    <li
                      key={index}
                      className="flex flex-col sm:flex-row items-start sm:items-center gap-4 my-1 p-4 bg-gray-50 dark:bg-dark-foreground rounded-lg shadow-sm hover:shadow-md transition-shadow"
                    >
                      {/* Product Image */}
                      <div
                        onClick={() =>
                          handleNavigateToProductDetails(orderItem)
                        }
                        className={`w-[300px] h-32 flex items-center justify-center bg-white dark:bg-dark-background rounded overflow-hidden cursor-pointer`}
                      >
                        <img
                          src={orderItem.productImage}
                          alt={orderItem.productName}
                          className="w-full h-full object-contain"
                        />
                      </div>

                      {/* Product Details & Pricing */}
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center w-full">
                        {orderItem?.sourceType === "travel" &&
                        orderItem?.flixbusData ? (
                          <div className="mt-4 w-full bg-white dark:bg-dark-foreground border dark:border-dark-foreground rounded-xl shadow p-4">
                            {/* Journey Header */}
                            <div className="flex flex-row justify-between items-center rounded-lg bg-gray-50 dark:bg-dark-background border dark:border-dark-foreground px-2 py-2 mb-2">
                              <div className="w-2/3">
                                <h4 className="text-lg font-bold text-green-700 flex items-center gap-2">
                                  <BsBusFrontFill color="blue" /> Flixbus Ticket
                                </h4>
                                {/*<p className="text-sm text-gray-600 mt-1">
                            {orderItem.productName}
                          </p>*/}
                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                  {/* Check if productName contains '|' and split it */}
                                  {orderItem.productName.includes("|") ? (
                                    <>
                                      <span className="text-sm text-gray-600 dark:text-gray-400 mt-1 font-semibold underline">
                                        OutBound:
                                      </span>
                                      <br />

                                      <span className="text-sm text-gray-600 dark:text-gray-400 mt-1 font-semibold">
                                        Departure:{" "}
                                        <span className="text-md ">
                                          {dayjs(
                                            orderItem.flixbusData.departure,
                                            "YYYY-MM-DD HH:mm"
                                          )
                                            .format("DD.MM.YYYY, HH.mm.ss")
                                            .replace(/:/g, ".")}
                                          {/* {dayjs(orderItem.flixbusData.departure, "YYYY-MM-DDTHH:mm:ss.SSSZ").format(
                                            "DD.MM.YYYY, HH.mm.ss"
                                          )} */}
                                        </span>
                                      </span>
                                      <br />
                                      <span className="text-green-700 font-semibold">
                                        {orderItem.productName
                                          .split("|")[0]
                                          .trim()}

                                        <br />
                                      </span>
                                      <span className="text-sm text-gray-600 dark:text-gray-400 mt-1 font-semibold">
                                        {/* Arrival:{orderItem.flixbusData.arrival} */}
                                        Arrival:{" "}
                                        <span className="text-md">
                                          {dayjs(
                                            orderItem.flixbusData.arrival,
                                            "YYYY-MM-DD HH:mm:ss"
                                          )
                                            .format("DD.MM.YYYY, HH.mm.ss")
                                            .replace(/:/g, ".")}
                                          {/* {dayjs(orderItem.flixbusData.arrival, "YYYY-MM-DDTHH:mm:ss.SSSZ").format(
                                            "DD.MM.YYYY, HH.mm.ss"
                                          )} */}
                                        </span>
                                      </span>
                                      <br />
                                      <br />
                                      <span className="text-sm text-gray-600 dark:text-gray-400 mt-1 font-semibold underline">
                                        InBound:
                                      </span>
                                      <br />
                                      <span className="text-sm text-gray-600 dark:text-gray-400 mt-1 font-semibold">
                                        Return Departure:
                                        <span className="text-md ">
                                          {dayjs(
                                            orderItem.flixbusData
                                              .returnDepartureDate
                                          ).format("DD.MM.YYYY, HH.mm.ss")}
                                        </span>
                                      </span>
                                      <br />
                                      <span className="text-green-700 font-semibold">
                                        {orderItem.productName
                                          .split("|")[1]
                                          .trim()}
                                      </span>
                                      <br />
                                      <span className="text-sm text-gray-600 dark:text-gray-400 mt-1 font-semibold">
                                        Return Arrival:
                                        <span className="text-md ">
                                          {dayjs(
                                            orderItem.flixbusData
                                              .returnArrivalDate
                                          ).format("DD.MM.YYYY, HH.mm.ss")}
                                        </span>
                                      </span>
                                    </>
                                  ) : (
                                    <>
                                      <span className="text-sm text-gray-600 dark:text-gray-400 mt-1 font-semibold">
                                        {/* Departure:
                                        {orderItem.flixbusData.departure} */}
                                        <span>Departure: </span>
                                        <span>
                                          {dayjs(
                                            orderItem.flixbusData.departure,
                                            "YYYY-MM-DD HH:mm:ss"
                                          )
                                            .format("DD.MM.YYYY, HH.mm.ss")
                                            .replace(/:/g, ".")}
                                        </span>
                                      </span>
                                      <br />
                                      <span className="text-green-700 font-semibold">
                                        {orderItem.productName}
                                        <br />
                                      </span>
                                      <span className="text-sm text-gray-600 dark:text-gray-400 mt-1 font-semibold">
                                        {/* Arrival:{orderItem.flixbusData.arrival} */}
                                        <span>Arrival: </span>
                                        <span>
                                          {dayjs(
                                            orderItem.flixbusData.arrival,
                                            "YYYY-MM-DD HH:mm:ss"
                                          )
                                            .format("DD.MM.YYYY, HH.mm.ss")
                                            .replace(/:/g, ".")}
                                        </span>
                                      </span>
                                    </>
                                  )}
                                </p>

                                <p className="text-sm text-gray-800 dark:text-gray-300 font-semibold">
                                  {Utils.formatEuro(orderItem.price)}
                                  {/* &nbsp; | &nbsp; Qty:{" "}
                            <span className="font-semibold">
                              {orderItem.quantity}
                            </span> */}
                                </p>
                              </div>
                              <div className="flex flex-col justify-between items-end gap-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Payment Status:
                                  </span>
                                  <span
                                    className={`px-3 py-1 rounded-full text-sm font-semibold border ${getStatusColor(
                                      orderData.paymentStatus
                                    )}`}
                                  >
                                    {formatOrderStatus(orderData.paymentStatus || "N/A")}
                                  </span>
                                </div>

                                {/* Delivery Status */}
                                <div className="flex items-center justify-between gap-1">
                                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Delivery Status:
                                  </span>
                                  <span
                                    className={`px-3 py-1 rounded-full text-sm font-semibold border ${getStatusColor(
                                      orderItem?.deliveryStatus
                                    )}`}
                                  >
                                    {orderItem?.deliveryStatus === "In_progress"
                                      ? "In-Progress"
                                      : formatOrderStatus(orderItem?.deliveryStatus || "N/A")}
                                  </span>
                                </div>

                                {/* Pending retry message */}
                                {orderItem?.orderStatus === "pending_retry" && (
                                  <div className="mt-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg px-3 py-2">
                                    <span className="text-xs text-amber-800 dark:text-amber-300">Delivery in progress. The system will automatically retry. Please check back shortly.</span>
                                  </div>
                                )}
                                {/* Delivery failed message */}
                                {orderItem?.deliveryStatus === "delivery_failed" && orderItem?.orderStatus !== "pending_retry" && (
                                  <div className="mt-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg px-3 py-2 text-xs text-red-800 dark:text-red-300">
                                    {/*
                                      * Written for the customer, not copied from
                                      * `failureReason`.
                                      *
                                      * That field is ours -- it has shown people
                                      * "Unexpected token 'o', \"no healthy
                                      * upstream\" is not valid JSON", cart UUIDs,
                                      * FlixBus's own unfilled %maxAge%
                                      * placeholder, and an internal note about an
                                      * order predating a fix. 67 items carry one.
                                      *
                                      * The exact text is still on the order for
                                      * Admin, where somebody can act on it. The
                                      * customer gets a sentence they can act on,
                                      * and anything unrecognised falls back to a
                                      * generic one rather than leaking.
                                      * See Utils/deliveryFailureMessage.
                                      */}
                                    {deliveryFailureMessage(
                                      orderItem?.failureReason,
                                    )}
                                    {/*
                                      * And the button. It existed only in the
                                      * non-travel half of the layout below, so a
                                      * failed travel booking told the customer to
                                      * request a refund and gave them nothing to
                                      * press. Same condition the other half uses,
                                      * so both behave alike.
                                      */}
                                    {orderItem?.refundEligible && (
                                      <div className="mt-2">
                                        <button
                                          className="px-4 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setOrderDetails(orderItem);
                                            setOrderNumberForRefundAll(orderItem.orderNumber);
                                            setOrderItemNumberForRefundAll(orderItem.itemNumber);
                                            setRefundReason(
                                              refundReasonForFailure(
                                                orderItem?.failureReason,
                                              ),
                                            );
                                            setIsRefundModalOpen(true);
                                          }}
                                        >
                                          Request Refund
                                        </button>
                                      </div>
                                    )}
                                    {orderItem?.refundState && (
                                      <div className="mt-2 text-blue-700 dark:text-blue-300">
                                        {orderItem.refundState.status === "refunded"
                                          ? `Refunded ${Utils.formatEuro(orderItem.refundState.amount)} to your original payment method.`
                                          : orderItem.refundState.status === "refund_declined"
                                          ? "Your refund request was not approved. Please contact support."
                                          : orderItem.refundState.status === "refund_processing"
                                          ? `${Utils.formatEuro(orderItem.refundState.amount)} is on its way back to you.`
                                          : `Refund requested for ${Utils.formatEuro(orderItem.refundState.amount)} — being reviewed.`}
                                      </div>
                                    )}
                                  </div>
                                )}
                                {orderItem?.deliveryStatus === "partially_delivered" && (
                                  <div className="mt-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-lg px-3 py-2 text-xs text-orange-800 dark:text-orange-300">
                                    Some items in this order were delivered. Others are being processed.
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Details Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              {/* Contact */}
                              <div className="p-3 border dark:border-dark-foreground rounded-lg bg-gray-50 dark:bg-dark-background">
                                <h5 className="flex text-md font-bold text-gray-800 dark:text-white mb-2 gap-2 underline">
                                  <MdContactPage size={25} /> Contact
                                  Information
                                </h5>
                                <p className="flex text-sm text-gray-700 dark:text-gray-300 break-all mb-2 gap-2">
                                  <MdEmail size={20} />{" "}
                                  {orderItem.flixbusData.contact?.email}
                                </p>
                                <p className="flex text-sm text-gray-700 dark:text-gray-300 gap-2">
                                  <FaPhoneAlt size={20} />{" "}
                                  {orderItem.flixbusData.contact?.phone}
                                </p>
                              </div>

                              {(() => {
                                const { labels, chosen } =
                                  getAssignedSeats(orderItem);
                                // The full breakdown when the confirmed order
                                // describes the journey; the flat list when it
                                // does not.
                                if (buildTrips(orderItem).length) {
                                  return <JourneyBreakdown orderItem={orderItem} />;
                                }
                                const byLeg = getSeatsByLeg(orderItem);
                                return (
                                  <div className="p-3 border dark:border-dark-foreground rounded-lg bg-gray-50 dark:bg-dark-background">
                                    <h5 className="flex items-center gap-2 text-md font-bold text-gray-800 dark:text-white underline">
                                      <MdEventSeat size="25" />{" "}
                                      {byLeg.length ? "Seats" : "Seat"}
                                    </h5>

                                    {/*
                                      One seat per ride, named against the ride
                                      it is on. A single list of numbers is
                                      unusable on a journey with changes: the
                                      passenger cannot tell which coach each
                                      one is for, or which direction.
                                    */}
                                    {byLeg.length > 0 ? (
                                      <div className="mt-2 space-y-3">
                                        {byLeg.map((trip, i) => (
                                          <div key={i}>
                                            {trip.title && (
                                              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                {trip.title}
                                              </p>
                                            )}
                                            <ul className="mt-1 space-y-1">
                                              {trip.legs.map((leg, k) => (
                                                <li
                                                  key={k}
                                                  className="flex items-start justify-between gap-3 text-sm"
                                                >
                                                  <span className="text-gray-700 dark:text-gray-300">
                                                    {leg.from && leg.to
                                                      ? `${leg.from} → ${leg.to}`
                                                      : `Ride ${k + 1}`}
                                                    {leg.vehicle && (
                                                      <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
                                                        ({leg.vehicle})
                                                      </span>
                                                    )}
                                                  </span>
                                                  <span className="font-semibold text-gray-800 dark:text-gray-200 whitespace-nowrap">
                                                    {leg.labels.join(", ")}
                                                  </span>
                                                </li>
                                              ))}
                                            </ul>
                                          </div>
                                        ))}
                                        {!chosen && (
                                          <p className="text-xs text-gray-500 dark:text-gray-400">
                                            Assigned by FlixBus — no seat was
                                            chosen at booking.
                                          </p>
                                        )}
                                      </div>
                                    ) : labels.length > 0 ? (
                                      <>
                                        <p className="mt-2 text-sm font-semibold text-gray-800 dark:text-gray-200">
                                          {labels.join(", ")}
                                        </p>
                                        {!chosen && (
                                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                            Assigned by FlixBus — no seat was
                                            chosen at booking.
                                          </p>
                                        )}
                                      </>
                                    ) : (
                                      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                                        Assigned at boarding. FlixBus has not
                                        given a seat number for this booking.
                                      </p>
                                    )}
                                  </div>
                                );
                              })()}

                              <div className="p-3 border dark:border-dark-foreground rounded-lg bg-gray-50 dark:bg-dark-background">
                                <h5 className="flex items-center gap-2 text-md font-bold text-gray-800 dark:text-white underline">
                                  <FaUsers size="25" /> Passengers
                                </h5>

                                {orderItem.productName.includes("|") ? (
                                  <>
                                    {/* Outbound Section */}
                                    <h6 className="mt-3 font-semibold text-gray-700 dark:text-gray-300 underline">
                                      Outbound:
                                    </h6>
                                    <ul className="space-y-1 text-sm text-gray-700 dark:text-gray-300 mt-1">
                                      {orderItem.flixbusData.passengers
                                        ?.slice(
                                          0,
                                          Math.ceil(
                                            orderItem.flixbusData.passengers
                                              .length / 2
                                          )
                                        )
                                        .map((p: any, idx: number) => (
                                          <li
                                            key={`outbound-${idx}`}
                                            className="flex items-center gap-1"
                                          >
                                            <span className="font-medium">
                                              {idx + 1}. {p.firstname}{" "}
                                              {p.lastname} ({p.gender}
                                              {fareType(p) ? `, ${fareType(p)}` : ""})
                                            </span>
                                          </li>
                                        ))}
                                    </ul>

                                    {/* Inbound Section */}
                                    <h6 className="mt-3 font-semibold text-gray-700 dark:text-gray-300 underline">
                                      Inbound:
                                    </h6>
                                    <ul className="space-y-1 text-sm text-gray-700 dark:text-gray-300 mt-1">
                                      {orderItem.flixbusData.passengers
                                        ?.slice(
                                          Math.ceil(
                                            orderItem.flixbusData.passengers
                                              .length / 2
                                          )
                                        )
                                        .map((p: any, idx: number) => (
                                          <li
                                            key={`inbound-${idx}`}
                                            className="flex items-center gap-1"
                                          >
                                            <span className="font-medium">
                                              {idx + 1}. {p.firstname}{" "}
                                              {p.lastname} ({p.gender}
                                              {fareType(p) ? `, ${fareType(p)}` : ""})
                                            </span>
                                          </li>
                                        ))}
                                    </ul>
                                  </>
                                ) : (
                                  // If personName does NOT contain '|', show all passengers normally
                                  <ul className="space-y-1 text-sm text-gray-700 dark:text-gray-300 mt-3">
                                    {orderItem.flixbusData.passengers?.map(
                                      (p: any, idx: number) => (
                                        <li
                                          key={idx}
                                          className="flex items-center gap-1"
                                        >
                                          <span className="font-medium">
                                            {idx + 1}. {p.firstname}{" "}
                                            {p.lastname} ({p.gender}
                                            {fareType(p) ? `, ${fareType(p)}` : ""})
                                          </span>
                                        </li>
                                      )
                                    )}
                                  </ul>
                                )}

                                <br />

                                {/* Extra Information */}
                                <h5 className="flex text-md font-bold text-gray-800 dark:text-white mb-2 gap-2 underline">
                                  <IoIosInformationCircleOutline size={25} />{" "}
                                  Extra Information
                                </h5>
                                <div className="mb-3">
                                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    Included per person:
                                  </p>
                                  <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 dark:text-gray-400 mt-1">
                                    <li>
                                      <span className="font-medium text-gray-800 dark:text-gray-300">
                                        1 Hand Luggage
                                      </span>{" "}
                                      | 7kg · 42×30×18 cm
                                    </li>
                                    <li>
                                      <span className="font-medium text-gray-800 dark:text-gray-300">
                                        1 Hold Luggage
                                      </span>{" "}
                                      | 20kg · 80×50×30 cm
                                    </li>
                                  </ul>
                                </div>
                                <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                                  <p>
                                    <span className="font-semibold text-gray-800 dark:text-white">
                                      Extra Luggage:
                                    </span>{" "}
                                    {orderItem.flixbusData.additionLuggageCount}{" "}
                                    (20kg · 80×50×30 cm)
                                  </p>
                                  <p>
                                    <span className="font-semibold text-gray-800 dark:text-white">
                                      Special Luggage:
                                    </span>{" "}
                                    {orderItem.flixbusData.bulkyLuggageCount}{" "}
                                    (30kg · 240 cm (x + y + z))
                                  </p>
                                </div>
                              </div>

                              {/* Ticket Details */}

                              {(() => {
                                const qrRes = orderItem.flixbusData.qrCodeResponse;
                                const hasUsableQR = qrRes && (
                                  qrRes.qrLinks?.qrCodeLocation?.linkPng ||
                                  (qrRes.qrItems && qrRes.qrItems.length > 0) ||
                                  qrRes.rawBase64
                                );
                                return !hasUsableQR;
                              })() ? (
                                /*
                                 * No QR code. There are two very different
                                 * reasons for that and they used to look
                                 * identical: a spinner and "your booking is
                                 * being confirmed".
                                 *
                                 * On an order whose delivery FAILED, nothing is
                                 * being confirmed and nothing ever will be --
                                 * FlixBus refused the booking. ORDE-102 sat on
                                 * that spinner permanently, telling a customer
                                 * whose booking had failed to refresh in a
                                 * minute. The order header said Delivery Failed
                                 * three lines above it.
                                 */
                                (() => {
                                  const deliveryState = String(
                                    orderItem?.deliveryStatus || "",
                                  ).toLowerCase();
                                  const bookingGaveUp =
                                    deliveryState === "delivery_failed" ||
                                    deliveryState === "failed" ||
                                    deliveryState === "cancelled" ||
                                    deliveryState === "rejected";

                                  if (bookingGaveUp) {
                                    return (
                                      <div className="p-3 border dark:border-dark-foreground rounded-lg bg-gray-50 dark:bg-dark-background">
                                        <h5 className="flex items-center gap-2 text-md font-bold text-gray-800 dark:text-white underline mb-2">
                                          <IoTicketSharp size={20} /> Boarding Pass
                                        </h5>
                                        <div className="flex flex-col items-center gap-2 py-4">
                                          <MdCancel
                                            size={28}
                                            className="text-red-500"
                                          />
                                          <p className="text-sm text-gray-700 dark:text-gray-300 text-center font-medium">
                                            No ticket was issued for this
                                            booking.
                                          </p>
                                          <p className="text-xs text-gray-500 dark:text-gray-400 text-center max-w-sm">
                                            The booking could not be completed
                                            with FlixBus, so there is no
                                            boarding pass to show. Please
                                            contact support about a refund, or
                                            book again.
                                          </p>
                                        </div>
                                      </div>
                                    );
                                  }

                                  // Still on its way -- the spinner is honest here.
                                  return (
                                    <div className="p-3 border dark:border-dark-foreground rounded-lg bg-gray-50 dark:bg-dark-background">
                                      <h5 className="flex items-center gap-2 text-md font-bold text-gray-800 dark:text-white underline mb-2">
                                        <IoTicketSharp size={20} /> Boarding Pass
                                      </h5>
                                      <div className="flex flex-col items-center gap-3 py-4">
                                        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 text-center font-medium">
                                          Your booking is being confirmed with
                                          FlixBus. This may take a few moments.
                                        </p>
                                        <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
                                          QR code and ticket will appear here
                                          once confirmed. Please refresh the
                                          page after a minute.
                                        </p>
                                      </div>
                                      {/* Cancel Booking disabled until QR arrives */}
                                      <div className="mt-3 flex justify-center">
                                        <button
                                          disabled
                                          className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-400 font-semibold rounded-lg cursor-not-allowed opacity-50"
                                          title="Cancel will be available once booking is confirmed"
                                        >
                                          <MdCancel size={20} /> Cancel Booking
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })()
                              ) : (
                                <div className="p-3 border dark:border-dark-foreground rounded-lg bg-gray-50 dark:bg-dark-background flex flex-col items-center justify-center">
                                  <h5 className="flex items-center gap-2 text-md font-bold text-gray-800 dark:text-white underline mb-2">
                                    <IoTicketSharp size={20} /> Boarding Pass
                                  </h5>

                                  {/* Show all QR codes - FlixBus may return multiple for large groups */}
                                  {(() => {
                                    const qrResponse = orderItem.flixbusData.qrCodeResponse;
                                    const qrItems = qrResponse?.qrItems || [];
                                    const linkPng = qrResponse?.qrLinks?.qrCodeLocation?.linkPng;
                                    const rawBase64 = qrResponse?.rawBase64;

                                    // 1. Use linkPng URL first (most reliable - CDN hosted PNG)
                                    if (linkPng) {
                                      return (
                                        <img
                                          src={linkPng}
                                          alt="FlixBus Boarding QR"
                                          className="w-32 h-32 object-contain border rounded mb-3"
                                        />
                                      );
                                    }

                                    // 2. Multiple QR codes for large groups (base64 is SVG XML)
                                    if (qrItems.length > 0) {
                                      return (
                                        <div className="flex flex-wrap gap-3 justify-center mb-3">
                                          {qrItems.map((qrData: any, qrIdx: number) => {
                                            const imgSrc =
                                              typeof qrData === "string"
                                                ? qrData.startsWith("http")
                                                  ? qrData
                                                  : qrData.startsWith("data:")
                                                  ? qrData
                                                  : `data:image/svg+xml;base64,${qrData}`
                                                : null;

                                            return imgSrc ? (
                                              <img
                                                key={qrIdx}
                                                src={imgSrc}
                                                alt={`Boarding QR ${qrIdx + 1}`}
                                                className="w-32 h-32 object-contain border rounded"
                                              />
                                            ) : null;
                                          })}
                                        </div>
                                      );
                                    }

                                    // 3. Fallback to rawBase64 (SVG XML format from FlixBus)
                                    if (rawBase64) {
                                      const src = rawBase64.startsWith("http")
                                        ? rawBase64
                                        : rawBase64.startsWith("data:")
                                        ? rawBase64
                                        : `data:image/svg+xml;base64,${rawBase64}`;
                                      return (
                                        <img
                                          src={src}
                                          alt="FlixBus Boarding QR"
                                          className="w-32 h-32 object-contain border rounded mb-3"
                                        />
                                      );
                                    }

                                    return <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">QR codes loading...</p>;
                                  })()}
                                  {orderItem?.flixbusData?.flixOrderDetails &&
                                    !orderItem?.flixbusData?.cancelResponse && (
                                      <button
                                        onClick={() => downloadTicket(orderItem)}
                                        disabled={ticketDownloading}
                                        className="flex items-center gap-2 text-blue-600 font-semibold hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        {ticketDownloading ? (
                                          <>
                                            <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                            Downloading...
                                          </>
                                        ) : (
                                          <>
                                            <IoTicketSharp /> Download PDF Ticket
                                          </>
                                        )}
                                      </button>
                                    )}
                                  <div className="mt-3">
                                    {orderItem?.flixbusData?.cancelResponse ? (
                                      //  Cancellation already initiated → show info badge (not a button)
                                      <div className="flex items-center gap-2 text-red-600 font-medium">
                                        <MdCancel
                                          size={20}
                                          className="text-red-600"
                                        />
                                        <span>Cancellation Initiated</span>
                                      </div>
                                    ) : hasJourneyDeparted(orderItem) ? (
                                      //  Already travelled → say so, rather than
                                      //  offering a cancellation that cannot happen
                                      <div className="flex flex-col gap-1">
                                        <button
                                          disabled
                                          className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-400 font-semibold rounded-lg cursor-not-allowed opacity-50 w-fit"
                                          title="This journey has already departed"
                                        >
                                          <MdCancel size={20} /> Cancel Booking
                                        </button>
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                          This journey has already departed, so
                                          it can no longer be cancelled.
                                        </span>
                                      </div>
                                    ) : orderItem?.flixbusData?.qrCodeResponse ? (
                                      //  Allow cancellation → show actionable button
                                      <button
                                        onClick={() => {
                                          setIsCancelOption(true);
                                          setFlixOrderDetails(
                                            orderItem?.flixbusData
                                              ?.flixOrderDetails
                                          );
                                          setSeatBookingDetails(
                                            orderItem?.flixbusData
                                              ?.seatAndTicketInfo
                                          );
                                        }}
                                        className="flex items-center gap-2 px-4 py-2 border border-red-600 text-red-600 font-semibold rounded-lg hover:bg-red-50 transition"
                                      >
                                        <MdCancel size={20} /> Cancel Booking
                                      </button>
                                    ) : null}
                                  </div>
                                </div>
                              )}

                              {/* Terms & Conditions */}
                              {(() => {
                                const infoData = orderItem?.flixbusData?.orderInfo || busOrderInfo;
                                const order = infoData?.order || infoData;
                                const attachments: any[] = order?.attachments || [];
                                const passengerRightsAtt = attachments.find(
                                  (att: any) => (att.rel || att.title || att.name || '').includes('passengers-rights')
                                );

                                return (
                                  <div className="p-3 border dark:border-dark-foreground rounded-lg bg-gray-50 dark:bg-dark-background md:col-start-3">
                                    <h5 className="flex items-center gap-2 text-md font-bold text-gray-800 dark:text-white underline mb-3">
                                      <IoIosInformationCircleOutline size={20} /> Terms and Conditions
                                    </h5>
                                    <div className="space-y-2">
                                      <a
                                        href="https://global.flixbus.com/general-terms-business-and-booking-conditions"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
                                      >
                                        <IoIosInformationCircleOutline size={16} />
                                        Terms and Conditions
                                      </a>
                                      <a
                                        href="https://www.flixbus.com/service/change-cancel-a-booking/cancellation-policy"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
                                      >
                                        <MdCancel size={14} />
                                        Cancellation Policy
                                      </a>
                                      {passengerRightsAtt && (
                                        <a
                                          href={passengerRightsAtt.href || passengerRightsAtt.link || passengerRightsAtt.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
                                        >
                                          <IoTicketSharp size={14} />
                                          Passenger Rights
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-3 gap-4 w-full px-2 py-3">
                            {/* Left Section */}
                            <div
                              className="w-full cursor-pointer"
                              onClick={() =>
                                handleNavigateToProductDetails(orderItem)
                              }
                            >
                              <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                                {orderItem.productName}
                                <span className="ml-1 text-md text-gray-500 dark:text-gray-400 font-medium">
                                  - {Utils.formatEuro(orderItem?.originalPrice || orderItem?.price)}
                                </span>
                                {!!(orderItem?.hasDiscount || (orderItem?.originalPrice && orderItem?.originalPrice > orderItem?.price) || (orderItem?.discountAmount && orderItem?.discountAmount > 0)) && (
                                  <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                                    {orderItem.discountType === 'percentage' && orderItem.discountValue
                                      ? `${orderItem.discountValue}% OFF`
                                      : orderItem.discountValue
                                        ? `${Utils.formatEuro(orderItem.discountValue)} OFF`
                                        : orderItem.discountAmount
                                          ? `${Utils.formatEuro(orderItem.discountAmount)} OFF`
                                          : `${Utils.formatEuro((orderItem.originalPrice || 0) - (orderItem.discountedPrice || orderItem.price || 0))} OFF`}
                                  </span>
                                )}
                              </h4>
                              {orderItem.accountNumber && (
                                <p className="text-sm italic font-bold text-blue-500">
                                  (+{orderItem?.accountNumber})
                                </p>
                              )}
                              {/* Denomination + Discount Price */}
                              {(orderItem?.hasDiscount || (orderItem?.originalPrice && orderItem?.originalPrice > orderItem?.price) || (orderItem?.discountAmount && orderItem?.discountAmount > 0)) ? (
                                <div className="flex flex-col mt-1">
                                  <div className="flex items-center gap-2 text-xs flex-wrap">
                                    <span className="text-gray-400 dark:text-gray-500 line-through">
                                      {Utils.formatEuro(orderItem.originalPrice || orderItem.price)}
                                    </span>
                                    <span className="text-green-600 font-semibold">
                                      {Utils.formatEuro(orderItem.discountedPrice || orderItem.price)}
                                    </span>
                                    <span className="text-green-600 text-[10px]">
                                      (You save {Utils.formatEuro(
                                        orderItem.discountAmount
                                          ? orderItem.discountAmount
                                          : ((orderItem.originalPrice || 0) - (orderItem.discountedPrice || orderItem.price || 0)) * orderItem.quantity
                                      )})
                                    </span>
                                    {orderData?.paymentMethod && (
                                      <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 text-[9px] font-semibold rounded">
                                        {orderData.paymentMethod === "BankTransfer" ? "Bank Transfer" : "Online"}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <p className="text-sm text-gray-700 dark:text-gray-300 font-medium mt-1">
                                  {Utils.formatEuro(orderItem?.price)}
                                </p>
                              )}
                              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                Qty:{" "}
                                <span className="font-semibold text-gray-800 dark:text-white">
                                  {orderItem.quantity}
                                </span>
                              </p>
                              {/* Price with VAT breakdown - only show Excl/Incl when tax exists */}
                              {orderItem.taxOfAll && orderItem.taxOfAll > 0 ? (
                                <>
                                  <p className="text-sm italic text-gray-500 dark:text-gray-400 font-bold">
                                    {Utils.formatEuro(orderItem.totalBeforeTax)}{" "}
                                    <span className="text-sm italic font-mono text-orange-500 font-bold">
                                      (Excl.VAT)
                                    </span>
                                  </p>
                                  <p className="text-sm italic text-gray-500 dark:text-gray-400 font-bold mt-1">
                                    {Utils.formatEuro(orderItem.totalPrice)}{" "}
                                    <span className="text-sm italic font-mono text-orange-500 font-bold">
                                      (Incl.VAT)
                                    </span>
                                  </p>
                                </>
                              ) : (
                                <p className="text-sm italic text-gray-500 dark:text-gray-400 font-bold">
                                  {Utils.formatEuro(orderItem.totalPrice)}
                                </p>
                              )}
                            </div>

                            {/* Middle Section */}
                            <div className="w-full gap-4 flex flex-col justify-start items-start">
                              {/* Payment Status */}
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                  Payment Status:
                                </span>
                                <span
                                  className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(
                                    orderItem.paymentStatus
                                  )}`}
                                >
                                  {formatOrderStatus(orderItem?.paymentStatus || "N/A")}
                                </span>
                              </div>

                              {/* Delivery Status */}
                              <div className="flex items-center gap-2 mt-2">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                  Delivery Status:
                                </span>
                                <span
                                  className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(
                                    orderItem.deliveryStatus
                                  )}`}
                                >
                                  {orderItem?.deliveryStatus === "In_progress"
                                    ? "In-Progress"
                                    : formatOrderStatus(
                                        orderItem?.deliveryStatus || "N/A"
                                      )}
                                </span>
                              </div>

                              {/* Order Status */}
                              <div className="flex items-center gap-2 mt-2">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                  Order Status:
                                </span>
                                <span
                                  className={`px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(
                                    orderItem?.orderStatus
                                  )}`}
                                >
                                  {formatOrderStatus(
                                    orderItem?.orderStatus || "Unknown"
                                  )}
                                </span>
                              </div>

                              {/* Pending Retry Banner */}
                              {orderItem?.orderStatus === "pending_retry" && (
                                <div className="mt-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg px-4 py-3">
                                  <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                                    Delivery in Progress
                                  </p>
                                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                                    We're working on delivering your item. The system will automatically retry. Please check back shortly.
                                  </p>
                                </div>
                              )}

                              {/* Refund Requested Banner */}
                              {orderItem?.orderStatus === "refund_requested" && (
                                <div className="mt-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-lg px-4 py-3">
                                  <div className="flex items-start gap-3">
                                    <div className="mt-0.5 text-orange-500">
                                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                    </div>
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-orange-800 dark:text-orange-300">
                                        Refund Requested
                                      </p>
                                      <p className="text-xs text-orange-600 dark:text-orange-400 mt-0.5">
                                        Your refund request has been submitted and is being reviewed. You will be notified once it is processed.
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Refund Declined Banner */}
                              {orderItem?.orderStatus === "refund_declined" && (
                                <div className="mt-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg px-4 py-3">
                                  <div className="flex items-start gap-3">
                                    <div className="mt-0.5 text-red-500">
                                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    </div>
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-red-800 dark:text-red-300">
                                        Refund Declined
                                      </p>
                                      <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">
                                        Your refund request has been reviewed and declined by the admin.
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Delivery Failed — Refund Eligible Banner */}
                              {/* A refund already exists for this item, so the
                                  Request Refund banner below is suppressed by
                                  refundEligible. Show where it stands instead --
                                  the button used to remain, the API rejected the
                                  duplicate, and the customer got no explanation. */}
                              {orderItem?.orderStatus === "delivery_failed" && orderItem?.refundState && (
                                <div className="mt-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg px-4 py-3">
                                  <div className="flex items-start gap-3">
                                    <div className="mt-0.5 text-blue-500">
                                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                    </div>
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                                        {orderItem.refundState.status === "refunded"
                                          ? "Refunded"
                                          : orderItem.refundState.status === "refund_declined"
                                          ? "Refund Declined"
                                          : orderItem.refundState.status === "refund_processing"
                                          ? "Refund In Progress"
                                          : "Refund Requested"}
                                      </p>
                                      <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                                        {orderItem.refundState.status === "refunded"
                                          ? `${Utils.formatEuro(orderItem.refundState.amount)} has been refunded to your original payment method.`
                                          : orderItem.refundState.status === "refund_declined"
                                          ? "Your refund request was not approved. Please contact support if you need help."
                                          : orderItem.refundState.status === "refund_processing"
                                          ? `${Utils.formatEuro(orderItem.refundState.amount)} is on its way back to your original payment method.`
                                          : `We have received your request for ${Utils.formatEuro(orderItem.refundState.amount)} and it is being reviewed.`}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )}


                              {orderItem?.orderStatus === "delivery_failed" && orderItem?.refundEligible && (
                                <div className="mt-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg px-4 py-3">
                                  <div className="flex items-start gap-3">
                                    <div className="mt-0.5 text-red-500">
                                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                      </svg>
                                    </div>
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-red-800 dark:text-red-300">
                                        Delivery Failed
                                      </p>
                                      <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">
                                        We were unable to deliver this item after multiple attempts. You can request a refund for this item.
                                      </p>
                                      <button
                                        className="mt-2 px-4 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setOrderDetails(orderItem);
                                          setOrderNumberForRefundAll(orderItem.orderNumber);
                                          setOrderItemNumberForRefundAll(orderItem.itemNumber);
                                          setRefundReason("Pin delivery failed after multiple retries");
                                          setIsRefundModalOpen(true);
                                        }}
                                      >
                                        Request Refund
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Right Section */}
                            <div className="flex flex-col justify-center sm:items-center md:items-end gap-3 sm:mt-0">
                              {/* View Pins Button */}
                              {orderItem?.pinsData &&
                                // orderItem?.orderStatus !== "refund_requested" &&
                                // orderItem?.orderStatus !== "refund_processing" &&
                                (() => {
                                  const inventoryPins =
                                    orderItem.pinsData.filter(
                                      (pin: any) => pin.collect === "inventory"
                                    );

                                  return inventoryPins.length > 0 &&
                                    wasPaid(orderData?.paymentStatus) ? (
                                    <button
                                      className="flex items-center gap-1 text-red-600 font-bold text-[11px] bg-red-100 rounded-full px-3 py-[3px] ml-3"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        showPinDetailsModal(orderItem);
                                      }}
                                    >
                                      <FaEye /> View Pins
                                    </button>
                                  ) : null;
                                })()}

                              {/* Review Section */}
                              <div
                                className={`flex flex-col text-right ${
                                  orderItem?.sourceType === "travel" &&
                                  orderItem?.flixbusData
                                    ? "opacity-50 pointer-events-none"
                                    : ""
                                }`}
                              >
                                <div className="flex items-center flex-col space-x-2">
                                  {wasPaid(orderData?.paymentStatus) && (
                                    <div className="flex gap-1 items-center">
                                      {Array.from({
                                        length: Math.floor(
                                          orderItem?.existingReview?.score || 0
                                        ),
                                      }).map((_, index) => (
                                        <Star key={index} color="#facc15" />
                                      ))}

                                      {(orderItem?.existingReview?.score || 0) %
                                        1 !==
                                        0 && <Star color="#facc15" />}

                                      {Array.from({
                                        length: emptyStars(
                                          orderItem?.existingReview?.score || 0
                                        ),
                                      }).map((_, index) => (
                                        <Star
                                          key={`empty-${index}`}
                                          color="#d1d5db"
                                        />
                                      ))}
                                      <span className="text-sm text-gray-700 dark:text-gray-300 font-semibold">
                                        {orderItem?.existingReview?.score.toFixed(
                                          1
                                        )}
                                      </span>
                                    </div>
                                  )}

                                  {orderItem?.existingReview ? (
                                    <>
                                      <Link
                                        to=""
                                        state={{ orderItem }}
                                        className="text-blue-500 text-sm hover:underline ml-2"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setIsOpenForReview(true);
                                          setOrderItem(orderItem);
                                        }}
                                      >
                                        Update this review
                                      </Link>
                                    </>
                                  ) : (
                                    wasPaid(orderData?.paymentStatus) && (
                                      <Link
                                        to=""
                                        state={{ orderItem }}
                                        className="text-blue-500 text-sm hover:underline ml-2 mt-1"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setIsOpenForReview(true);
                                          setOrderItem(orderItem);
                                        }}
                                      >
                                        Write a review
                                      </Link>
                                    )
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              {/* Documents Section with Tabs */}
              {(creditMemos && creditMemos.length > 0) || (refundInvoices && refundInvoices.length > 0) ? (
                <div className="mt-6 bg-white dark:bg-dark-foreground rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="px-4 pt-4 pb-0">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-3">Documents</h3>
                    {/* Tabs */}
                    <div className="flex border-b border-gray-200 dark:border-gray-700 gap-0">
                      <button
                        onClick={() => setActiveDocTab(0)}
                        className={`px-4 py-2.5 text-sm font-medium transition-all border-b-2 ${
                          activeDocTab === 0
                            ? "border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400"
                            : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                        }`}
                      >
                        Credit Notes {creditMemos && creditMemos.length > 0 && (
                          <span className="ml-1 bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 text-xs font-semibold px-1.5 py-0.5 rounded-full">
                            {creditMemos.length}
                          </span>
                        )}
                      </button>
                      <button
                        onClick={() => setActiveDocTab(1)}
                        className={`px-4 py-2.5 text-sm font-medium transition-all border-b-2 ${
                          activeDocTab === 1
                            ? "border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400"
                            : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                        }`}
                      >
                        Refund Invoices {refundInvoices && refundInvoices.length > 0 && (
                          <span className="ml-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-semibold px-1.5 py-0.5 rounded-full">
                            {refundInvoices.length}
                          </span>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Tab Content */}
                  <div className="p-4">
                    {/* Credit Notes Tab */}
                    {activeDocTab === 0 && (
                      <div>
                        {creditMemos && creditMemos.length > 0 ? (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="bg-orange-50 dark:bg-orange-900/20 text-left">
                                  <th className="px-3 py-2 font-semibold text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">Credit Memo #</th>
                                  {/* Refunds are issued per PIN, so ten identical cards produce ten rows
                                      with the same amount and date -- only `batch`, an internal number,
                                      differed. The product is what makes the table navigable. */}
                                  <th className="px-3 py-2 font-semibold text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">Product</th>
                                  <th className="px-3 py-2 font-semibold text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">Batch</th>
                                  <th className="px-3 py-2 font-semibold text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 text-right">Amount</th>
                                  <th className="px-3 py-2 font-semibold text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">Date</th>
                                  <th className="px-3 py-2 font-semibold text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 text-center">Document</th>
                                </tr>
                              </thead>
                              <tbody>
                                {creditMemos.map((cm: any) => (
                                  <tr key={cm._id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                                    <td className="px-3 py-2 font-medium text-gray-800 dark:text-white border border-gray-200 dark:border-gray-700">
                                      {cm.creditMemoNumber || "—"}
                                    </td>
                                    <td className="px-3 py-2 text-gray-800 dark:text-white border border-gray-200 dark:border-gray-700">{cm.productLabel || "—"}</td>
                                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
                                      {cm.batch || "—"}
                                    </td>
                                    <td className="px-3 py-2 text-gray-800 dark:text-white font-medium text-right border border-gray-200 dark:border-gray-700">
                                      {Utils.formatEuro((cm.amount || 0).toFixed(2))}
                                    </td>
                                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
                                      {dayjs(cm.createdAt).format("DD.MM.YYYY, HH:mm")}
                                    </td>
                                    <td className="px-3 py-2 border border-gray-200 dark:border-gray-700">
                                      {cm.creditMemoNumber ? (
                                        <div className="flex gap-1 justify-center">
                                          <button
                                            onClick={() => handleViewDocument("creditMemo", cm.creditMemoNumber, "en")}
                                            disabled={docLoading !== null}
                                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition disabled:opacity-50"
                                          >
                                            {docLoading === `creditMemo-${cm.creditMemoNumber}-en` ? (
                                              <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                                            ) : (
                                              <FaFileAlt className="w-3 h-3" />
                                            )}
                                            EN
                                          </button>
                                          <button
                                            onClick={() => handleViewDocument("creditMemo", cm.creditMemoNumber, "de")}
                                            disabled={docLoading !== null}
                                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition disabled:opacity-50"
                                          >
                                            {docLoading === `creditMemo-${cm.creditMemoNumber}-de` ? (
                                              <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                                            ) : (
                                              <FaFileAlt className="w-3 h-3" />
                                            )}
                                            DE
                                          </button>
                                        </div>
                                      ) : (
                                        <span className="text-gray-400 dark:text-gray-500">—</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 dark:text-gray-400 py-2">No credit notes found for this order.</p>
                        )}
                      </div>
                    )}

                    {/* Refund Invoices Tab */}
                    {activeDocTab === 1 && (
                      <div>
                        {refundInvoices && refundInvoices.length > 0 ? (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="bg-green-50 dark:bg-green-900/20 text-left">
                                  <th className="px-3 py-2 font-semibold text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">Refund Invoice #</th>
                                  <th className="px-3 py-2 font-semibold text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">Product</th>
                                  <th className="px-3 py-2 font-semibold text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">Batch</th>
                                  <th className="px-3 py-2 font-semibold text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 text-right">Amount</th>
                                  <th className="px-3 py-2 font-semibold text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">Date</th>
                                  <th className="px-3 py-2 font-semibold text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 text-center">Document</th>
                                </tr>
                              </thead>
                              <tbody>
                                {refundInvoices.map((ri: any) => (
                                  <tr key={ri._id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                                    <td className="px-3 py-2 font-medium text-gray-800 dark:text-white border border-gray-200 dark:border-gray-700">
                                      {ri.refundInvoiceNumber || "—"}
                                    </td>
                                    <td className="px-3 py-2 text-gray-800 dark:text-white border border-gray-200 dark:border-gray-700">{ri.productLabel || "—"}</td>
                                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
                                      {ri.batch || "—"}
                                    </td>
                                    <td className="px-3 py-2 text-gray-800 dark:text-white font-medium text-right border border-gray-200 dark:border-gray-700">
                                      {Utils.formatEuro((ri.amount || 0).toFixed(2))}
                                    </td>
                                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
                                      {dayjs(ri.createdAt).format("DD.MM.YYYY, HH:mm")}
                                    </td>
                                    <td className="px-3 py-2 border border-gray-200 dark:border-gray-700">
                                      {ri.refundInvoiceNumber ? (
                                        <div className="flex gap-1 justify-center">
                                          <button
                                            onClick={() => handleViewDocument("refundInvoice", ri.refundInvoiceNumber, "en")}
                                            disabled={docLoading !== null}
                                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition disabled:opacity-50"
                                          >
                                            {docLoading === `refundInvoice-${ri.refundInvoiceNumber}-en` ? (
                                              <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                                            ) : (
                                              <FaFileAlt className="w-3 h-3" />
                                            )}
                                            EN
                                          </button>
                                          <button
                                            onClick={() => handleViewDocument("refundInvoice", ri.refundInvoiceNumber, "de")}
                                            disabled={docLoading !== null}
                                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition disabled:opacity-50"
                                          >
                                            {docLoading === `refundInvoice-${ri.refundInvoiceNumber}-de` ? (
                                              <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                                            ) : (
                                              <FaFileAlt className="w-3 h-3" />
                                            )}
                                            DE
                                          </button>
                                        </div>
                                      ) : (
                                        <span className="text-gray-400 dark:text-gray-500">—</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 dark:text-gray-400 py-2">No refund invoices found for this order.</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : null}

              {/* Details */}
              <Suspense>
                <DealsSection
                  categoryIds={orderData?.orderItems[0].categoryId || []}
                  _id={orderData?.orderItems[0]?.categoryId[0]}
                  title={"Related products"}
                />
              </Suspense>

              <ViewPinsModal
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                pinLoading={pinLoading}
                pinData={pinData}
                orderDetails={orderDetails}
                onRefund={(batch: string) => {
                  // Single-pin refund: clear any leftover "refund all" routing
                  // state so Submit doesn't misroute to refund-all (which would
                  // refund every pin) after a previous Refund All action.
                  setOrderNumberForRefundAll(null);
                  setOrderItemNumberForRefundAll(null);
                  setSelectedBatch(batch);
                  setIsRefundModalOpen(true);
                }}
                onRefundAll={(orderNumber, itemNumber) => {
                  setSelectedBatch(null);
                  setOrderNumberForRefundAll(orderNumber);
                  setOrderItemNumberForRefundAll(itemNumber);
                  setIsRefundModalOpen(true);
                }}
                paymentMethod={orderData?.paymentMethod}
              />

              <Modal
                isOpen={isOpenForReview}
                onClose={() => setIsOpenForReview(false)}
                isActionBtn={false}
                dialogClassName="p-0 w-full min-w-[320px] sm:min-w-[450px] max-w-sm overflow-hidden align-middle transition-all transform bg-white dark:bg-dark-background shadow-xl rounded-2xl"
              >
                <ProductReview
                  orderItem={orderItem}
                  orderData={orderData}
                  onClose={() => {
                    setIsOpenForReview(false);
                    // getMyAllOrdersTrigger(`page=1&pagesize=10`);
                    getOrderDetailsTrigger(orderNumber);
                    // onReviewSuccess?.();
                  }}
                />
              </Modal>

              <Modal
                isOpen={isCancelOption}
                onClose={() => setIsCancelOption(false)}
                title="Cancel Booking"
                isActionBtn={true}
                showBackButton={false}
                onConfirm={() => {
                  handleCancelBooking();
                }}
                isConfirming={isConfirming}
              >
                <div className="text-md text-gray-700 dark:text-gray-300">
                  <p className="text-md text-gray-700 dark:text-gray-300">
                    Are you sure you want to cancel this booking?
                  </p>
                  {/*
                    Say what happens to the money before the click, not after.
                    This modal asked for confirmation and mentioned money not at
                    all, so the only figures a customer could find were on
                    FlixBus's own policy page -- which describes FlixBus's fare
                    and knows nothing of our service fee or of the booking fee
                    FlixBus keeps. Hence "it says 100% refund but I got less".
                    No percentages here: FlixBus does not tell us the rules, and
                    the amount is only known once the ticket is cancelled.
                  */}
                  <ul className="mt-3 space-y-1.5 text-sm text-gray-600 dark:text-gray-400 list-disc list-inside">
                    <li>
                      Your seat is released immediately and cannot be
                      reinstated.
                    </li>
                    <li>
                      The refundable amount is set by FlixBus under their
                      cancellation policy, and is confirmed once the booking is
                      cancelled.
                    </li>
                    <li>
                      Booking and service fees are non-refundable.
                    </li>
                    <li>
                      Any refund is returned to your original payment method
                      after review, and is not instant.
                    </li>
                  </ul>
                </div>
              </Modal>

              <Modal
                isOpen={isRefundModalOpen}
                onClose={() => setIsRefundModalOpen(false)}
                isActionBtn={false}
                dialogClassName="p-0 w-full min-w-[320px] sm:min-w-[450px] max-w-sm overflow-hidden align-middle transition-all transform bg-white dark:bg-dark-foreground shadow-xl rounded-2xl"
              >
                <div className="p-6">
                  {/* Header */}
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
                    Request a Refund
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
                    We’re sorry to hear you’d like to cancel this order. Please
                    tell us why so we can process your refund smoothly.
                  </p>

                  {/* Reason Input */}
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Reason for refund:
                  </label>
                  <textarea
                    rows={4}
                    className="w-full border border-gray-300 dark:border-dark-foreground dark:bg-dark-background dark:text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="E.g. Received damaged product, wrong item delivered, etc."
                    value={refundReason}
                    onChange={(e) => setRefundReason(e.target.value)}
                    disabled={isRefundLoading || isRefundAllLoading}
                  />

                  {/* Action Buttons */}
                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      onClick={() => setIsRefundModalOpen(false)}
                      disabled={isRefundLoading || isRefundAllLoading}
                      className={`px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-dark-foreground rounded-md ${
                        isRefundLoading || isRefundAllLoading
                          ? "opacity-50 cursor-not-allowed"
                          : "hover:bg-gray-100 dark:hover:bg-gray-700"
                      }`}
                    >
                      Cancel
                    </button>
                    <button
                      disabled={!refundReason.trim() || isRefundLoading || isRefundAllLoading}
                      className={`px-4 py-2 text-sm font-medium text-white rounded-md flex items-center justify-center gap-2 transition-all min-w-[100px] ${
                        refundReason.trim()
                          ? isRefundLoading || isRefundAllLoading
                            ? "bg-blue-400 cursor-not-allowed"
                            : "bg-blue-500 hover:bg-blue-600"
                          : "bg-blue-300 cursor-not-allowed"
                      }`}
                      onClick={() => {
                        if (isRefundLoading || isRefundAllLoading) return; // prevent double click

                        if (orderNumberForRefundAll) {
                          handleRefundForAll(
                            orderData,
                            orderNumberForRefundAll,
                            orderItemNumberForRefundAll,
                            refundReason
                          );
                        } else {
                          handleRefund(orderData, refundReason, selectedBatch);
                        }
                      }}
                    >
                      {isRefundLoading || isRefundAllLoading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Submitting...</span>
                        </>
                      ) : (
                        "Submit"
                      )}
                    </button>
                  </div>
                </div>
              </Modal>

              {/* Bank Details Popup */}
              <Modal
                isOpen={showBankDetailsPopup}
                onClose={() => {
                  setShowBankDetailsPopup(false);
                  setCopiedField(null);
                }}
                isActionBtn={false}
                dialogClassName="p-0 w-full min-w-[320px] sm:min-w-[420px] max-w-md overflow-hidden align-middle transition-all transform bg-white dark:bg-dark-foreground shadow-xl rounded-2xl"
              >
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
                        Bank Account Details
                      </h2>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Transfer your payment to the account below
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {bankDetails?.bankName && (
                      <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                        <span className="text-sm text-gray-500 dark:text-gray-400">Bank Name</span>
                        <span className="text-sm font-medium text-gray-800 dark:text-white">{bankDetails.bankName}</span>
                      </div>
                    )}
                    {bankDetails?.accountHolderName && (
                      <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                        <span className="text-sm text-gray-500 dark:text-gray-400">Account Holder</span>
                        <span className="text-sm font-medium text-gray-800 dark:text-white">{bankDetails.accountHolderName}</span>
                      </div>
                    )}
                    {bankDetails?.iban && (
                      <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                        <span className="text-sm text-gray-500 dark:text-gray-400">IBAN</span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-800 dark:text-white font-mono">{bankDetails.iban}</span>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(bankDetails.iban);
                              setCopiedField("iban");
                              setTimeout(() => setCopiedField(null), 2000);
                            }}
                            className="text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition"
                            title="Copy IBAN"
                          >
                            {copiedField === "iban" ? (
                              <span className="text-green-500 text-xs font-medium">Copied!</span>
                            ) : (
                              <FaRegCopy className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                    {bankDetails?.bic && (
                      <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                        <span className="text-sm text-gray-500 dark:text-gray-400">BIC / SWIFT</span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-800 dark:text-white font-mono">{bankDetails.bic}</span>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(bankDetails.bic);
                              setCopiedField("bic");
                              setTimeout(() => setCopiedField(null), 2000);
                            }}
                            className="text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition"
                            title="Copy BIC"
                          >
                            {copiedField === "bic" ? (
                              <span className="text-green-500 text-xs font-medium">Copied!</span>
                            ) : (
                              <FaRegCopy className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                    {bankDetails?.accountNumber && (
                      <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                        <span className="text-sm text-gray-500 dark:text-gray-400">Account Number</span>
                        <span className="text-sm font-medium text-gray-800 dark:text-white font-mono">{bankDetails.accountNumber}</span>
                      </div>
                    )}
                    {bankDetails?.sortCode && (
                      <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                        <span className="text-sm text-gray-500 dark:text-gray-400">Sort Code</span>
                        <span className="text-sm font-medium text-gray-800 dark:text-white font-mono">{bankDetails.sortCode}</span>
                      </div>
                    )}
                    {bankDetails?.notes && (
                      <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                        <p className="text-xs text-amber-700 dark:text-amber-400 font-medium mb-1">Note</p>
                        <p className="text-sm text-amber-800 dark:text-amber-300">{bankDetails.notes}</p>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => {
                      setShowBankDetailsPopup(false);
                      setCopiedField(null);
                    }}
                    className="mt-5 w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition"
                  >
                    Close
                  </button>
                </div>
              </Modal>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default MyOrderDetails;
